import React, { useState, useEffect } from "react";
import {
  Box, Typography, CircularProgress, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, Switch,
  FormControlLabel, Grid, Chip, Stack, Card, CardContent,
  IconButton
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import Swal from "sweetalert2";
import apiClient from "@/api/client";

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({});

  // Permissions list - same as before but using role names
  const permissions = [
    { id: "events_coordinator", label: "Events Coordinator", color: "#1976d2" },
    { id: "community_service_coordinator", label: "Community Service Coordinator", color: "#2e7d32" },
    { id: "lunch_dana_coordinator", label: "Lunch Dana Coordinator", color: "#ed6c02" },
    { id: "breakfast_dana_coordinator", label: "Breakfast Dana Coordinator", color: "#f57c00" },
    { id: "meditate_with_us_coordinator", label: "Meditate With Us Coordinator", color: "#9c27b0" },
    { id: "dhamma_talks_coordinator", label: "Dhamma Talks Coordinator", color: "#d32f2f" },
    { id: "arama_poojawa_coordinator", label: "Arama Poojawa Coordinator", color: "#0288d1" },
    { id: "build_up_hermitage_coordinator", label: "Build Up Hermitage Coordinator", color: "#7b1fa2" },
    { id: "donation_coordinator", label: "Donation Coordinator", color: "#c2185b" },
  ];

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/users');
      const data = response.data;
      // Filter out admin users
      const filteredUsers = data.users.filter(
        (user) => !user.roles?.some(role => role.name === 'admin')
      );
      setUsers(filteredUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Failed to fetch users. Please try again.",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleView = (user) => {
    setSelectedUser(user);
    setViewDialogOpen(true);
  };

  const handleEdit = (user) => {
    // Prepare form data with roles
    const userRoles = user.roles?.map(role => role.name) || [];
    const permissionsObj = {};
    permissions.forEach(perm => {
      permissionsObj[perm.id] = userRoles.includes(perm.id) ? 1 : 0;
    });
    
    setEditFormData({
      ...user,
      ...permissionsObj,
      user_type: user.user_type || (userRoles.length > 0 ? "Coordinator" : "User")
    });
    setEditDialogOpen(true);
  };

  const handleDelete = (user) => {
    Swal.fire({
      icon: "warning",
      title: "Are you sure?",
      text: `You are about to delete ${user.name}. This action cannot be undone.`,
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await apiClient.delete(`/soft-delete-user/${user.id}`);
          Swal.fire({
            icon: "success",
            title: "Deleted!",
            text: `${user.name} has been deleted.`,
            confirmButtonText: "OK",
          });
          setUsers(users.filter((u) => u.id !== user.id));
        } catch (error) {
          console.error("Error deleting user:", error);
          Swal.fire({
            icon: "error",
            title: "Error!",
            text: "Failed to delete user. Please try again.",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  const handleEditChange = (event) => {
    const { name, value, checked } = event.target;
    
    if (name === 'user_type') {
      setEditFormData({
        ...editFormData,
        [name]: value,
      });
    } else if (permissions.some(p => p.id === name)) {
      // Handle permission switches
      setEditFormData({
        ...editFormData,
        [name]: checked ? 1 : 0
      });
    } else {
      // Handle regular fields
      setEditFormData({
        ...editFormData,
        [name]: value
      });
    }
  };

  const handleEditSubmit = async () => {
    try {
      // Build coordinator_roles array from permission flags
      const coordinator_roles = [];
      permissions.forEach(perm => {
        if (editFormData[perm.id] === 1) {
          coordinator_roles.push(perm.id);
        }
      });

      const payload = {
        fullName: editFormData.name,
        email: editFormData.email,
        gender: editFormData.gender,
        nic: editFormData.nic,
        role: editFormData.user_type,
        coordinator_roles: editFormData.user_type === 'Coordinator' ? coordinator_roles : []
      };

      await apiClient.put(`/edit-user/${editFormData.id}`, payload);

      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "User updated successfully!",
        confirmButtonText: "OK",
      });

      setEditDialogOpen(false);
      fetchUsers();
    } catch (error) {
      console.error("Error updating user:", error);
      let errorMessage = "Failed to update user. Please try again.";
      if (error.response && error.response.data) {
        if (error.response.data.errors) {
          errorMessage = Object.entries(error.response.data.errors)
            .map(([field, messages]) => `${field}: ${messages.join(", ")}`)
            .join("\n");
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: errorMessage,
        confirmButtonText: "OK",
      });
    }
  };

  // Define columns for DataGrid - exactly as before
  const columns = [
    { 
      field: 'name', 
      headerName: 'Name', 
      width: 200,
      sortable: true 
    },
    { 
      field: 'email', 
      headerName: 'Email', 
      width: 250,
      sortable: true 
    },
    { 
      field: 'nic', 
      headerName: 'NIC', 
      width: 150,
      sortable: true 
    },
    { 
      field: 'gender', 
      headerName: 'Gender', 
      width: 120,
      sortable: true 
    },
    { 
      field: 'user_type', 
      headerName: 'User Type', 
      width: 150,
      sortable: true,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          variant="outlined"
          color={params.value === 'Coordinator' ? 'primary' : 'default'}
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            onClick={() => handleView(params.row)}
            color="info"
            size="small"
            title="View"
          >
            <VisibilityIcon />
          </IconButton>
          <IconButton
            onClick={() => handleEdit(params.row)}
            color="primary"
            size="small"
            title="Edit"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            onClick={() => handleDelete(params.row)}
            color="error"
            size="small"
            title="Delete"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" component="div">
              User List {users.length > 0 && `(${users.length} users)`}
            </Typography>
            <Button 
              variant="outlined" 
              onClick={fetchUsers}
              startIcon={<RefreshIcon />}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: "text.secondary" }}>
              All Users
            </Typography>
            <hr />
            <Box sx={{ height: 'calc(100vh - 300px)', width: '100%', mt: 2 }}>
              <DataGrid
                rows={users}
                columns={columns}
                pageSizeOptions={[10, 25, 50, 100]}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } },
                  sorting: { sortModel: [{ field: 'name', sort: 'asc' }] },
                }}
                disableRowSelectionOnClick
                getRowId={(row) => row.id}
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold", textAlign: "center" }}>
          User Details
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {[
              ["Name", selectedUser?.name],
              ["Email", selectedUser?.email],
              ["NIC", selectedUser?.nic],
              ["Gender", selectedUser?.gender],
              ["User Type", selectedUser?.user_type],
            ].map(([label, value], idx) => (
              <React.Fragment key={idx}>
                <Grid item xs={5}>
                  <Typography fontWeight="bold">{label}:</Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography>{value || 'N/A'}</Typography>
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
          
          {selectedUser?.user_type === "Coordinator" && (
            <>
              <Typography variant="h6" sx={{ mt: 3, mb: 2, fontWeight: "bold" }}>
                Permissions
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {permissions.map((permission, index) => {
                  const hasPermission = selectedUser.roles?.some(r => r.name === permission.id);
                  return (
                    <Box 
                      key={index} 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        p: 1,
                        bgcolor: hasPermission ? 'primary.50' : 'transparent',
                        borderRadius: 1,
                      }}
                    >
                      <Chip
                        label={permission.label}
                        color={hasPermission ? "primary" : "default"}
                        variant={hasPermission ? "filled" : "outlined"}
                        size="small"
                        sx={{ minWidth: 250 }}
                      />
                      {hasPermission ? (
                        <Typography variant="caption" color="success.main" fontWeight="bold">
                          ✓ Granted
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          ✗ Not Granted
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Name"
            name="name"
            value={editFormData.name || ""}
            onChange={handleEditChange}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Email"
            name="email"
            value={editFormData.email || ""}
            onChange={handleEditChange}
            margin="normal"
          />
          <TextField
            fullWidth
            label="NIC"
            name="nic"
            value={editFormData.nic || ""}
            onChange={handleEditChange}
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Gender</InputLabel>
            <Select
              label="Gender"
              name="gender"
              value={editFormData.gender || ""}
              onChange={handleEditChange}
            >
              <MenuItem value="Male">Male</MenuItem>
              <MenuItem value="Female">Female</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>User Type</InputLabel>
            <Select
              label="User Type"
              name="user_type"
              value={editFormData.user_type || "User"}
              onChange={handleEditChange}
            >
              <MenuItem value="User">User</MenuItem>
              <MenuItem value="Coordinator">Coordinator</MenuItem>
            </Select>
          </FormControl>

          {editFormData.user_type === "Coordinator" && (
            <>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                Permissions
              </Typography>
              <Grid container spacing={1}>
                {permissions.map((permission, index) => (
                  <Grid item xs={12} key={index}>
                    <FormControlLabel
                      control={
                        <Switch
                          name={permission.id}
                          checked={!!editFormData[permission.id]}
                          onChange={handleEditChange}
                        />
                      }
                      label={permission.label}
                    />
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserList;